import {Injectable} from '@angular/core';
import {AbstractControl, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {CompetitorSimple} from '../../../../../types/competitor';
import {Crew, CrewSample} from '../../../../../types/crew';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {LocalStorageService} from '../../../../../../api/services/localStorage';
import {HttpHandlingService} from '../../../../../../api/services/http-handling.service';
import {MatDialogRef} from '@angular/material/dialog';
import {EditCrewCardInfo} from '../../../../../types/editCrewCardInfo';
import {EditCrewCardComponent} from '../edit-crew-card.component';
import {LoggedUser} from '../../../../../types/loggedUser';
import {SERVER_API_URL} from '../../../../../types/app.constants';

@Injectable({
  providedIn: 'root'
})
export class EditCrewCardService {

  constructor(private http: HttpClient, private localStorage: LocalStorageService, private httpHandlingService: HttpHandlingService) {}

  crewCardForm = new UntypedFormGroup({
    crewNumber: new UntypedFormControl('', Validators.required),
    driver: new UntypedFormControl('', Validators.required),
    navigator: new UntypedFormControl('', Validators.required),
    additionalCompetitor: new UntypedFormControl(''),
  });

  loggedUser: LoggedUser;

  crewId: number;
  drivers: CompetitorSimple[];
  navigators: CompetitorSimple[];
  additionalCompetitors: CompetitorSimple[];
  availableCompetitors: CompetitorSimple[];
  numbers: number[];

  serverIsDown = false;

  dialogRef: MatDialogRef<EditCrewCardComponent>;

  initializeComponentData(data: EditCrewCardInfo, dialogRef: MatDialogRef<EditCrewCardComponent>): void {
    if (this.localStorage.get('loggedUser') !== null) {
      this.loggedUser = this.localStorage.get('loggedUser') as LoggedUser;
    }
    this.crewId = data.crew.id;
    this.drivers = data.crewNumbersWithCompetitors.availableCompetitors;
    this.navigators = data.crewNumbersWithCompetitors.availableCompetitors;
    this.availableCompetitors = data.crewNumbersWithCompetitors.availableCompetitors;
    this.additionalCompetitors = data.crew.additionalCompetitors;
    this.additionalCompetitors = this.additionalCompetitors.filter(
      (item: CompetitorSimple) => item.id !== data.crew.driver.id && item.id !== data.crew.navigator.id);
    this.availableCompetitors = this.availableCompetitors.filter(
      (item: CompetitorSimple) => item.id !== data.crew.driver.id && item.id !== data.crew.navigator.id);
    if (data.crew.number !== null || undefined) {
      this.crewCardForm.setValue({
        crewNumber: data.crew.number,
        driver: data.crew.driver,
        navigator: data.crew.navigator,
        additionalCompetitor: null
      });
    }
    this.dialogRef = dialogRef;
    this.numbers = Array(99).fill(1).map((x, i) => 900 + i + 1)
      .filter((item: number) => !(item !== data.crew.number && data.crewNumbersWithCompetitors.crewNumbers.includes(item)));
  }

  /**
   * function for evaluation of available competitors based on additional competitors
   */
  processAvailableCompetitors(): void {
    const additionalCompetitor = this.crewCardForm.get('additionalCompetitor').value as CompetitorSimple;
    this.additionalCompetitors.push(additionalCompetitor);
    this.availableCompetitors.splice(this.availableCompetitors.indexOf(additionalCompetitor), 1);
    this.crewCardForm.patchValue({additionalCompetitor: null});
  }

  /**
   * function for disabling add additional competitor, if additional competitor
   * dropdown is empty
   */
  additionalCompetitorFieldIsEmpty(): boolean {
    return this.crewCardForm.get('additionalCompetitor').value === null || this.crewCardForm.get('additionalCompetitor').value === '';
  }

  /**
   * function for removing desired competitor from additional competitors list
   * @param competitor for evaluation of competitor to delete
   */
  deleteAdditionalCompetitor(competitor: CompetitorSimple): void {
    this.availableCompetitors.push(competitor);
    this.additionalCompetitors.splice(this.additionalCompetitors.indexOf(competitor), 1);
  }

  /**
   * function for re-filtering all dropdowns after new input is emitted
   * @param data for input data provided to dialog from start
   */
  refilterDropdowns(data: EditCrewCardInfo): void {
    const driver = this.crewCardForm.get('driver').value as CompetitorSimple;
    const navigator = this.crewCardForm.get('navigator').value as CompetitorSimple;
    this.drivers = data.crewNumbersWithCompetitors.availableCompetitors.filter((item: CompetitorSimple) => item.id !== navigator.id &&
      this.additionalCompetitors.filter((competitor: CompetitorSimple) => item.id === competitor.id).length === 0);
    this.navigators = data.crewNumbersWithCompetitors.availableCompetitors.filter((item: CompetitorSimple) => item.id !== driver.id &&
      this.additionalCompetitors.filter((competitor: CompetitorSimple) => item.id === competitor.id).length === 0);
    this.availableCompetitors = data.crewNumbersWithCompetitors.availableCompetitors.filter(
      (item: CompetitorSimple) => item.id !== driver.id && item.id !== navigator.id &&
        this.additionalCompetitors.filter((competitor: CompetitorSimple) => item.id === competitor.id).length === 0);
  }

  /**
   * function which provides error messages in the process of updating crew card
   * @param fieldControl with information about desired field (driver, navigator, ...)
   */
  getErrorMessage(fieldControl: AbstractControl): string {
    return this.httpHandlingService.handleUserErrorOutputAbstract(fieldControl);
  }

  /**
   * function for creating the crew with provided data
   */
  onSaveCrew(): void {
    const crewNumber = this.crewCardForm.get('crewNumber').value as number;
    const driver = this.crewCardForm.get('driver').value as CompetitorSimple;
    const navigator = this.crewCardForm.get('navigator').value as CompetitorSimple;
    const editCrewURL = SERVER_API_URL + '/api/crewsWithCompetitorIds';
    const additionalCompetitorsIds = this.onGetAdditionalCompetitorsIds(this.additionalCompetitors);
    const crew = new Crew(this.crewId, crewNumber, driver.id, navigator.id, additionalCompetitorsIds);
    this.http.put<any>(editCrewURL, crew,
      {headers: this.httpHandlingService.authorizationHeaders()}).subscribe(result => {
        const crewSample = new CrewSample(this.crewId, crewNumber, driver, navigator, this.additionalCompetitors);
        let activeInCrews = this.localStorage.get('loggedCompetitorCrews') as CrewSample[];
        activeInCrews = activeInCrews.filter((crewInActiveCrews: CrewSample) => crewInActiveCrews.id !== this.crewId);
        activeInCrews.push(crewSample);
        this.localStorage.set('loggedCompetitorCrews', activeInCrews);
      },
      (error: HttpErrorResponse) => { this.handleCreateErrors(error); },
      () => { this.dialogRef.close(); window.location.reload(); });
  }

  /**
   * function for retrieving additional competitors ids
   * @param additionalCompetitors for evaluation of additional competitors
   */
  onGetAdditionalCompetitorsIds(additionalCompetitors: CompetitorSimple[]): number[] {
    const additionalCompetitorsIds = [];
    for (const additionalCompetitor of additionalCompetitors) {
      additionalCompetitorsIds.push(additionalCompetitor.id);
    }
    return additionalCompetitorsIds;
  }

  /**
   * function for handling HTTP errors in the process of newly added crew
   */
  handleCreateErrors(error: HttpErrorResponse): void {
    if (error.status === 0) {
      this.serverIsDown = true;
    }
  }
}
